import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Camera, Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable upload board for job-attached photos (unlimited).
 * kind: "materials" | "equipment"
 */
export default function JobPhotoBoard({
  job,
  kind,
  title,
  subtitle,
  canUpload,
  canDeleteAny = false,
  onChanged,
  colorScheme = "indigo",
  testIdPrefix,
}) {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const photos = (job[`${kind}_photos`] || []).filter((p) => !p.is_deleted);
  const prefix = testIdPrefix || `${kind}-photos`;

  const uploadFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      let last = null;
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(
          `/jobs/${job.id}/boards/${kind}/photos`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        last = data;
      }
      if (last) onChanged?.(last);
      toast.success(`${files.length} foto caricat${files.length === 1 ? "a" : "e"}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (pid) => {
    if (!confirm("Rimuovere questa foto?")) return;
    try {
      const { data } = await api.delete(`/jobs/${job.id}/boards/${kind}/photos/${pid}`);
      onChanged?.(data);
      toast.success("Foto rimossa");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const colors = {
    indigo: { border: "border-indigo-200", title: "text-indigo-900", icon: "text-indigo-600", btn: "bg-indigo-600 hover:bg-indigo-700" },
    violet: { border: "border-violet-200", title: "text-violet-900", icon: "text-violet-600", btn: "bg-violet-600 hover:bg-violet-700" },
  }[colorScheme] || { border: "border-slate-200", title: "text-slate-900", icon: "text-slate-600", btn: "bg-slate-700 hover:bg-slate-800" };

  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  const token = (() => {
    try { return JSON.parse(localStorage.getItem("auth") || "{}").access_token || ""; } catch { return ""; }
  })();

  return (
    <section className={`bg-white rounded-2xl border-2 ${colors.border} p-6`} data-testid={`${prefix}-panel`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className={`font-display font-bold ${colors.title} flex items-center gap-2`}>
          <Camera className={`w-4 h-4 ${colors.icon}`} /> {title}
          <span className="text-xs font-mono text-slate-400">({photos.length})</span>
        </h3>
        {canUpload && (
          <>
            <input
              type="file"
              ref={fileRef}
              onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
              accept="image/jpeg,image/png,image/webp,image/heic,image/gif"
              multiple
              className="hidden"
              data-testid={`${prefix}-upload-input`}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className={`inline-flex items-center gap-2 ${colors.btn} text-white text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-60`}
              data-testid={`${prefix}-upload-button`}
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploading ? "Carico…" : "Carica foto"}
            </button>
          </>
        )}
      </div>
      {subtitle && <p className="text-[11px] text-slate-500 mb-4">{subtitle}</p>}

      {photos.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-400 italic bg-slate-50 border border-dashed border-slate-200 rounded-xl">
          Nessuna foto caricata.
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
          {photos.map((p) => (
            <div key={p.id} className="relative group" data-testid={`${prefix}-item-${p.id}`}>
              <img
                src={`${API_BASE}/api/jobs/${job.id}/boards/${kind}/photos/${p.id}/download${token ? `?t=${encodeURIComponent(token)}` : ""}`}
                alt=""
                className="aspect-square w-full object-cover rounded-xl border border-slate-200"
                loading="lazy"
                onError={(e) => {
                  // fallback: fetch with auth header via blob
                  if (!e.target.dataset.retried) {
                    e.target.dataset.retried = "1";
                    fetch(`${API_BASE}/api/jobs/${job.id}/boards/${kind}/photos/${p.id}/download`, {
                      headers: { Authorization: `Bearer ${token}` },
                    })
                      .then((r) => r.blob())
                      .then((b) => { e.target.src = URL.createObjectURL(b); })
                      .catch(() => {});
                  }
                }}
              />
              {(canDeleteAny || user?.role === "admin" || user?.id === p.uploaded_by) && (
                <button
                  onClick={() => remove(p.id)}
                  className="absolute top-1 right-1 bg-rose-600 hover:bg-rose-700 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                  data-testid={`${prefix}-delete-${p.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
